import {
  Box,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";
import { useThemeMode } from "../../context/ThemeContext";
import { authCardShadow, hideScrollbar } from "../../utils/theme";

/**
 * Shared shell for every public/user-facing auth screen.
 *
 * Layout is a flex column with three regions:
 *   - sticky header  (title / subtitle / optional custom `header`, + theme toggle)
 *   - scrollable middle (the ONLY scroll container; hidden scrollbar)
 *   - sticky footer  (action buttons — never scroll away on tall forms)
 *
 * On mobile it renders full-screen for a native-app feel; on desktop a rounded,
 * height-capped card. Pass `onSubmit` to make the whole card a <form> so footer
 * submit buttons and the fields in `children` share one form.
 *
 * Intentionally renders NO product logo — the consent screen shows the client's
 * own logo, and other public screens use a neutral text header.
 */
function AuthLayout({
  title,
  subtitle,
  header,
  children,
  footer,
  onSubmit,
  maxWidth = "xs",
  showThemeToggle = false,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { mode, toggleTheme } = useThemeMode();

  return (
    <Dialog
      open
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth
      {...(onSubmit ? { component: "form", onSubmit, noValidate: true } : {})}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.15)",
          backdropFilter: "blur(2px)",
        },
      }}
      PaperProps={{
        sx: {
          m: fullScreen ? 0 : { xs: 2, sm: 3 },
          width: fullScreen ? "100%" : { xs: "calc(100% - 32px)", sm: "450px" },
          maxWidth: fullScreen ? "100%" : "450px",
          height: fullScreen ? "100%" : "auto",
          maxHeight: fullScreen ? "100%" : "92vh",
          borderRadius: fullScreen ? 0 : 3,
          boxShadow: (t) => authCardShadow(t),
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Sticky header */}
      <Box sx={{ flexShrink: 0, position: "relative", px: 3, pt: 3, pb: 1.5 }}>
        {showThemeToggle && (
          <IconButton
            onClick={toggleTheme}
            size="small"
            aria-label="Toggle light/dark mode"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "text.secondary",
            }}
          >
            {mode === "light" ? (
              <DarkModeOutlined fontSize="small" />
            ) : (
              <LightModeOutlined fontSize="small" />
            )}
          </IconButton>
        )}
        {header}
        {title && (
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 18, sm: 20 },
              fontWeight: 600,
              color: "text.primary",
              textAlign: "center",
            }}
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: { xs: 13, sm: 14 },
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Scrollable middle — the only scroll container, scrollbar hidden */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          px: 3,
          py: 1,
          ...hideScrollbar,
        }}
      >
        {children}
      </Box>

      {/* Sticky footer */}
      {footer && (
        <Box sx={{ flexShrink: 0, px: 3, pt: 1.5, pb: 3 }}>{footer}</Box>
      )}
    </Dialog>
  );
}

export default AuthLayout;
